/**
 * Calendar-date arithmetic for the metrics windows.
 *
 * Every value here is a `YYYY-MM-DD` string, never a Date carrying a clock:
 * a metric date is a label the server already bucketed (invariant 41), and
 * a Date constructed from one in the browser's zone shifts by a day for
 * anybody west of UTC. Arithmetic goes through UTC midnight, where a day is
 * always exactly 24 hours, and comes straight back out as a string.
 *
 * "Today" is the business calendar's, Asia/Kolkata -- the same answer
 * `sage_metrics::business_today` gives the server, so a window the page
 * asks for and a week the server computes agree about which day it is.
 */

const IST = new Intl.DateTimeFormat("en-CA", {
  timeZone: "Asia/Kolkata",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

/** Today on the business calendar. `en-CA` formats as YYYY-MM-DD. */
export const todayIst = (): string => IST.format(new Date());

const toUtc = (iso: string): number => {
  const [y, m, d] = iso.split("-").map(Number);
  return Date.UTC(y, m - 1, d);
};

const fromUtc = (ms: number): string => new Date(ms).toISOString().slice(0, 10);

export const addDays = (iso: string, n: number): string => fromUtc(toUtc(iso) + n * 86400000);

/** Inclusive length of a window, in days. */
export const spanDays = (from: string, to: string): number =>
  Math.round((toUtc(to) - toUtc(from)) / 86400000) + 1;

export const firstOfMonth = (iso: string): string => `${iso.slice(0, 8)}01`;

/** 0 = Monday ... 6 = Sunday, the ISO week the server also uses. */
export const weekdayIndex = (iso: string): number => (new Date(toUtc(iso)).getUTCDay() + 6) % 7;

export const isIsoDate = (s: string | null): s is string =>
  s !== null && /^\d{4}-\d{2}-\d{2}$/.test(s) && !Number.isNaN(toUtc(s));
